import { saveManager, type LeaderboardEntry } from './SaveManager';

/**
 * Shared-score boundary. The game works immediately with a local fallback;
 * setting VITE_LEADERBOARD_API switches submissions and reads to a hosted
 * service without changing the Phaser scenes.
 */
// The deployed site proxies a tiny JSON-backed service at /api. Keeping the
// browser fallback means local development still works when that service is
// not running.
const API_BASE = ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_LEADERBOARD_API ?? '/api').replace(/\/$/, '');

export interface LeaderboardSnapshot {
  map: LeaderboardEntry[];
  overall: LeaderboardEntry[];
  source: 'local' | 'remote';
}

class LeaderboardService {
  private localSyncPromise?: Promise<void>;

  get local(): boolean {
    return !API_BASE;
  }

  async submit(entry: LeaderboardEntry): Promise<{ mapRank: number; overallRank: number; source: 'local' | 'remote' }> {
    if (API_BASE) {
      const controller = new AbortController();
      const timeout = globalThis.setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(`${API_BASE}/scores`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(entry),
          signal: controller.signal,
        });
        if (response.ok) {
          const result = (await response.json()) as { mapRank?: number; overallRank?: number };
          globalThis.clearTimeout(timeout);
          return { mapRank: result.mapRank ?? 0, overallRank: result.overallRank ?? 0, source: 'remote' };
        }
      } catch {
        // Fall through to local storage so a temporary network outage never
        // blocks the result screen or discards a player's score.
      }
      globalThis.clearTimeout(timeout);
    }
    const rank = saveManager.recordLeaderboardEntry(entry);
    return { ...rank, source: 'local' };
  }

  /** Retry locally recorded runs whenever a menu/command deck opens after an offline result. */
  syncLocalScores(): Promise<void> {
    if (!API_BASE) return Promise.resolve();
    if (this.localSyncPromise) return this.localSyncPromise;
    this.localSyncPromise = this.pushLocalScores().finally(() => {
      this.localSyncPromise = undefined;
    });
    return this.localSyncPromise;
  }

  private async pushLocalScores(): Promise<void> {
    const entries = new Map<string, LeaderboardEntry>();
    for (const list of Object.values(saveManager.data.leaderboards)) {
      for (const entry of list ?? []) entries.set(entry.id, entry);
    }
    const pending = [...entries.values()]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 200);
    for (const entry of pending) {
      const controller = new AbortController();
      const timeout = globalThis.setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(`${API_BASE}/scores`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(entry),
          signal: controller.signal,
        });
        globalThis.clearTimeout(timeout);
        if (!response.ok) break;
      } catch {
        globalThis.clearTimeout(timeout);
        break;
      }
    }
  }

  async read(mapId: string): Promise<LeaderboardSnapshot> {
    if (API_BASE) {
      const controller = new AbortController();
      const timeout = globalThis.setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(`${API_BASE}/leaderboards?mapId=${encodeURIComponent(mapId)}`, { signal: controller.signal });
        if (response.ok) {
          const result = (await response.json()) as { map?: LeaderboardEntry[]; overall?: LeaderboardEntry[] };
          globalThis.clearTimeout(timeout);
          return { map: result.map ?? [], overall: result.overall ?? [], source: 'remote' };
        }
      } catch {
        /* local fallback below */
      }
      globalThis.clearTimeout(timeout);
    }
    return {
      map: saveManager.getLeaderboard(mapId),
      overall: saveManager.getLeaderboard('overall'),
      source: 'local',
    };
  }
}

export const leaderboardService = new LeaderboardService();
